<template>
    <div class="flex flex-col relative">
        <GmapMap
            v-show="state"
            ref="gmap"
            :center="center"
            :zoom="zoom"
            class="h-full"
            :clickable="true"
            :options="options"
            @center_changed="handleMapChange"
            @idle="handleMapChange"
        >
            <GmapCluster
                v-if="state"
                :zoom-on-click="true"
                :max-zoom="zoomOnSelect"
                :styles="[
                    {
                        textColor: '#003F6B',
                        textSize: '14',
                        fontWeight: '700',
                        fontFamily: 'Gotham, Helvetica, sans-serif',
                        url: '/dist/images/geosearch/cluster-small.png',
                        height: 80,
                        width: 80,
                        anchorText: [-2, 0]
                    },
                    {
                        textColor: '#003F6B',
                        textSize: '16',
                        fontWeight: '700',
                        fontFamily: 'Gotham, Helvetica, sans-serif',
                        url: '/dist/images/geosearch/cluster-large.png',
                        height: 96,
                        width: 96,
                        anchorText: [-2, 0]
                    }
                ]"
            >
                <GmapMarker
                    v-for="item in state.items"
                    :key="item.id"
                    :position="item._geoloc"
                    :clickable="true"
                    :icon="getMarkerIcon(item)"
                    @click="$emit('set-active-marker', item)"
                />
            </GmapCluster>

            <GmapInfoWindow
                v-if="activeMarker && !isMobile"
                :options="infoWindowOptions"
                :position="activeMarker._geoloc"
            />
        </GmapMap>

        <template v-if="activeMarker && isMobile">
            <!-- <MapViewProvider :item="activeMarker" @opened="scrollIntoView" /> -->
            <MapViewProvider :item="activeMarker" @close="$emit('set-active-marker', undefined)" />
        </template>
    </div>
</template>

<script>
import kebabCase from 'lodash/kebabCase';
// import debounce from 'lodash/debounce';
import { createWidgetMixin } from 'vue-instantsearch';
import { connectGeoSearch } from 'instantsearch.js/es/connectors';
import { gmapApi } from 'vue2-google-maps';
import MapViewProvider from './MapViewProvider';
import styles from './styles';

export default {
    components: {
        MapViewProvider
    },

    mixins: [createWidgetMixin({ connector: connectGeoSearch })],

    props: {
        activeMarker: {
            type: Object,
            default: null
        },

        mapDefaults: {
            type: Object,
            default: null
        },

        useGeoLocation: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            zoom: null,
            center: null,

            zoomOnSelect: 14,

            options: {
                maxZoom: 16,
                zoomControl: true,
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false,
                disableDefaultUI: false,
                styles
            }
        };
    },

    computed: {
        google: gmapApi,

        isMobile() {
            return this.$mq === 'mobile';
        },

        infoWindowOptions() {
            const content = `
                <div class="text-base font-bold text-blue-500 bg-white">
                    <p>${this.activeMarker.title}</p>
                </div>`;

            return {
                content,
                pixelOffset: {
                    width: 0,
                    height: -70
                }
            };
        }
    },

    watch: {
        state() {
            this.setIconPositions();
        }
    },

    created() {
        if (this.mapDefaults) {
            this.zoom = this.mapDefaults.zoom;
            this.center = {
                lat: this.mapDefaults.lat,
                lng: this.mapDefaults.lng
            };
        }
    },

    mounted() {
        // Only pan to user location once per session. Dont retrigger when swapping between map/list.
        const goToUserLocation = !sessionStorage.getItem('userLocationLoaded');
        if (navigator.geolocation && this.useGeoLocation && goToUserLocation) {
            this.$refs.gmap.$mapPromise.then((map) => {
                navigator.geolocation.getCurrentPosition((position) => {
                    // Temp set to VIC
                    // this.center = { lat: -37.8143, lng: 144.963 };
                    this.zoom = this.showSidebarZoom;
                    this.center = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    map.panTo(this.center);
                    sessionStorage.setItem('userLocationLoaded', 'true');
                });
            });
        }
    },

    methods: {
        scrollIntoView() {
            // Scroll map to top
            this.$nextTick(() => {
                this.$refs.gmap.$el.scrollIntoView({ block: 'center', scrollBehavior: 'smooth' });
            });
        },

        handleMapChange() {
            const bounds = { ...this.$refs.gmap.$mapObject.getBounds() };
            const zoom = this.$refs.gmap.$mapObject.getZoom();

            let center = { ...this.$refs.gmap.$mapObject.getCenter() };
            center = {
                lat: center.lat(),
                lng: center.lng()
            };

            let boundsNorthEast = {
                ...this.$refs.gmap.$mapObject.getBounds().getNorthEast()
            };
            boundsNorthEast = {
                lat: boundsNorthEast.lat(),
                lng: boundsNorthEast.lng()
            };

            if (zoom && bounds) {
                this.zoom = zoom;
                this.$emit('report-map', { zoom: this.zoom, bounds, center, boundsNorthEast });
            }
        },

        getMarkerIcon(marker) {
            const type = kebabCase(marker['categories.lvl0'][0]);

            return this.activeMarker && this.activeMarker.id === marker.id
                ? `/dist/images/geosearch/pin-active-${type}.png`
                : `/dist/images/geosearch/pin-${type}.png`;
        },

        // Set center of map to latlng received from places autocomplete and trigger refinement
        adjustMap(geometry) {
            this.$refs.gmap.$mapObject.fitBounds(geometry.viewport);
            if (this.zoom > 14) this.zoom = 14;
        },

        setIconPositions() {
            this.$nextTick(() => {
                this.$refs.gmap.$mapPromise.then((map) =>
                    map.setOptions({
                        zoomControlOptions: { position: this.google.maps.ControlPosition.TOP_RIGHT }
                    })
                );
            });
        }
    }
};
</script>

<style scoped>
::v-deep .gm-style-iw-d + button {
    display: none;
}
</style>
